import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Link,
  Typography,
  Button,
  Alert,
  Divider,
} from '@mui/material';
import { RefreshOutlined, OpenInNewOutlined } from '@mui/icons-material';
import { fetchCurrentAds, regenerateAdCopy, CurrentAdData } from '../../helpers/adEvaluationData';
import moment from 'moment';
import { formatGermanTime } from '../../helpers/germanTime';

const CurrentTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<CurrentAdData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [regenerating, setRegenerating] = useState(false);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchCurrentAds();
      setData(result);
    } catch (err) {
      console.error('Error fetching current ads:', err);
      setError('Fehler beim Laden der aktuellen Ads');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleRegenerate = async () => {
    if (!data?.article || data.article.type === 'empty' || !data.article.product_id) return;
    setRegenerating(true);
    try {
      await regenerateAdCopy(data.article.type, data.article.product_id);
      await loadData();
    } catch (err) {
      console.error('Error regenerating ad copy:', err);
      setError('Fehler beim Regenerieren der Ad Copy');
    } finally {
      setRegenerating(false);
    }
  };

  const typeChip = (type: string) => {
    if (type === 'webinar') return <Chip label="Webinar" color="primary" size="small" />;
    if (type === 'plx') return <Chip label="PLX" color="secondary" size="small" />;
    return <Chip label="Leer" size="small" />;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!data) return null;

  return (
    <Grid container spacing={3}>
      {/* DOI Selection */}
      <Grid item xs={12} md={6}>
        <Card sx={{ backgroundColor: '#ffffff' }}>
          <CardContent>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <Typography variant="h6" fontWeight="bold">
                DOI-Seite Werbung
              </Typography>
              {typeChip(data.doi.type)}
            </Box>

            {data.doi.type === 'empty' ? (
              <Box>
                <Typography color="text.secondary">
                  Kein Produkt aktiv — weder Webinar noch PLX-Kampagne qualifiziert sich.
                </Typography>
                {data.doi_skip_reason && (
                  <Alert severity="info" sx={{ mt: 2 }} variant="outlined">
                    {data.doi_skip_reason}
                  </Alert>
                )}
              </Box>
            ) : (
              <Box>
                <InfoRow label="Produkt" value={data.doi.product_name || '–'} />
                <InfoRow label="Concept" value={data.doi.concept || '–'} />
                <InfoRow label="Produkt-ID" value={data.doi.product_id || '–'} />
                {data.doi.start_date && (
                  <InfoRow
                    label="Start"
                    value={formatGermanTime(data.doi.start_date, 'DD.MM.YYYY HH:mm')}
                  />
                )}
                <InfoRow label="Banner" value={data.doi.banner_pattern || '–'} mono />

                {data.doi.landing_url && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" fontWeight="bold" mb={1}>
                      Ziel-URL:
                    </Typography>
                    <Link
                      href={data.doi.landing_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="hover"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        wordBreak: 'break-all',
                      }}
                    >
                      {data.doi.landing_url}
                      <OpenInNewOutlined sx={{ fontSize: 14 }} />
                    </Link>
                  </>
                )}

                {data.doi_skip_reason && (
                  <Alert severity="info" sx={{ mt: 2 }} variant="outlined">
                    {data.doi_skip_reason}
                  </Alert>
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>

      {/* Article Selection */}
      <Grid item xs={12} md={6}>
        <Card sx={{ backgroundColor: '#ffffff' }}>
          <CardContent>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <Typography variant="h6" fontWeight="bold">
                Artikel Ad
              </Typography>
              {typeChip(data.article.type)}
            </Box>

            {data.article.type === 'empty' ? (
              <Typography color="text.secondary">
                Kein Produkt aktiv — weder Webinar noch PLX-Kampagne qualifiziert sich.
              </Typography>
            ) : (
              <Box>
                <InfoRow label="Produkt" value={data.article.product_name || '–'} />
                <InfoRow label="Concept" value={data.article.concept || '–'} />
                <InfoRow label="Produkt-ID" value={data.article.product_id || '–'} />
                <InfoRow label="Banner" value={data.article.banner_pattern || '–'} mono />

                <Divider sx={{ my: 2 }} />

                {data.article.cached_copy ? (
                  <Box>
                    <Typography variant="subtitle2" fontWeight="bold" mb={1}>
                      Cached Ad Copy (Vorschau mit &quot;Musteraktie&quot;):
                    </Typography>
                    <Box sx={{ backgroundColor: '#f5f5f5', p: 2, borderRadius: 1 }}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {data.article.cached_copy.headline.replace(
                          /\{ASSET_NAME\}/g,
                          'Musteraktie',
                        )}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
                        {data.article.cached_copy.body.replace(/\{ASSET_NAME\}/g, 'Musteraktie')}
                      </Typography>
                      {data.article.landing_url ? (
                        <Link
                          href={data.article.landing_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          underline="hover"
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                            mt: 1.5,
                            fontWeight: 'bold',
                            color: 'primary.main',
                          }}
                        >
                          {data.article.cached_copy.cta_text.replace(
                            /\{ASSET_NAME\}/g,
                            'Musteraktie',
                          )}
                          <OpenInNewOutlined sx={{ fontSize: 14 }} />
                        </Link>
                      ) : (
                        <Typography
                          variant="body2"
                          sx={{
                            mt: 1.5,
                            fontWeight: 'bold',
                            color: 'primary.main',
                          }}
                        >
                          {data.article.cached_copy.cta_text.replace(
                            /\{ASSET_NAME\}/g,
                            'Musteraktie',
                          )}
                        </Typography>
                      )}
                    </Box>
                    <Box display="flex" alignItems="center" gap={2} mt={1}>
                      <Typography variant="caption" color="text.secondary">
                        Generiert:{' '}
                        {/* article_ad_cache.generated_at is written by sequelize NOW() and so
                            holds real UTC — unlike start_date above. Plain moment() is correct. */}
                        {moment(data.article.cached_copy.generated_at).format('DD.MM.YYYY HH:mm')}
                        {data.article.cached_copy.model_used &&
                          ` | Modell: ${data.article.cached_copy.model_used}`}
                      </Typography>
                    </Box>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<RefreshOutlined />}
                      onClick={handleRegenerate}
                      disabled={regenerating}
                      sx={{ mt: 1 }}
                    >
                      {regenerating ? 'Regeneriert...' : 'Regenerieren'}
                    </Button>
                  </Box>
                ) : (
                  <Typography color="text.secondary">Keine gecachte Ad Copy vorhanden.</Typography>
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

function InfoRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <Box display="flex" gap={1} mb={0.5}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100 }}>
        {label}:
      </Typography>
      <Typography
        variant="body2"
        sx={mono ? { fontFamily: 'monospace', fontSize: '0.8rem' } : undefined}
      >
        {value}
      </Typography>
    </Box>
  );
}

export default CurrentTab;
